/**
 * Pure helpers for the "Usage & credits" settings screen (native
 * `UsagePanel.tsx` and web `UsagePanel.web.tsx`). Numbers come from
 * `selectSubscriptionCreditUsageDisplay`; nothing here invents a value.
 *
 * Free plans have one daily pool (resets daily, UTC). Paid plans have a
 * monthly pool plus a daily pacing cap, so they get both rows. A `null`
 * limit means the plan has no cap on that axis: the row says "Unlimited" and
 * draws no bar.
 */
import {
  formatCreditAmount,
  type SubscriptionCreditUsageDisplay,
} from './subscription-credit-usage';

export type UsageLimitKind = 'monthly' | 'daily';

export interface UsageLimitRow {
  kind: UsageLimitKind;
  /** i18n key (common) for the row title. */
  labelKey: string;
  limit: number | null;
  remaining: number | null;
  /** 0-100, or null when the row is unlimited. */
  percentRemaining: number | null;
  resetAt: string | null;
  unlimited: boolean;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function selectUsageLimitRows(
  display: SubscriptionCreditUsageDisplay,
): UsageLimitRow[] {
  if (!display.hasCreditUsage) return [];

  const monthly: UsageLimitRow = {
    kind: 'monthly',
    labelKey: 'settings.usageScreen.monthlyLimit',
    limit: display.monthlyLimit,
    remaining: display.monthlyRemaining,
    percentRemaining: display.monthlyLimit === null ? null : display.monthlyPercentRemaining,
    resetAt: display.monthlyResetAt,
    unlimited: display.monthlyLimit === null,
  };
  const daily: UsageLimitRow = {
    kind: 'daily',
    labelKey: 'settings.usageScreen.dailyLimit',
    limit: display.dailyLimit,
    remaining: display.dailyRemaining,
    percentRemaining: display.dailyLimit === null ? null : display.dailyPercentRemaining,
    resetAt: display.dailyResetAt,
    unlimited: display.dailyLimit === null,
  };

  // Free has no monthly pool: a null cycle limit there means "no such pool",
  // not "unlimited", so the row is left out rather than shown as Unlimited.
  if (display.tier === 'free') {
    return display.monthlyLimit === null ? [daily] : [monthly, daily];
  }
  return [monthly, daily];
}

/**
 * The balance shown under "Credits remaining": the monthly pool on paid
 * plans, the daily pool on Free (which has no monthly pool).
 */
export function selectCreditsRemaining(
  display: SubscriptionCreditUsageDisplay,
): number | null {
  const usesMonthlyPool = display.tier !== 'free' || display.monthlyLimit !== null;
  return usesMonthlyPool ? display.monthlyRemaining : display.dailyRemaining;
}

/**
 * Whole-number percentage for the label. Floors, so "100% remaining" only
 * appears when nothing is used, but never shows 0% while credits remain.
 */
export function displayPercentRemaining(percent: number | null): number | null {
  if (percent === null || !Number.isFinite(percent)) return null;
  const clamped = Math.max(0, Math.min(100, percent));
  if (clamped > 0 && clamped < 1) return 1;
  return Math.floor(clamped);
}

/** Width of the filled part of the bar, 0-100. */
export function barFillPercent(percent: number | null): number {
  if (percent === null || !Number.isFinite(percent)) return 0;
  return Math.max(0, Math.min(100, percent));
}

export function formatPercentRemaining(
  percent: number | null,
  locale: string,
  t: Translate,
): string | null {
  const whole = displayPercentRemaining(percent);
  if (whole === null) return null;
  const formatted = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(whole / 100);
  return t('settings.usageScreen.percentRemaining', { percent: formatted });
}

/** "9,880 credits" / "Unlimited", pluralized through i18next `count`. */
export function formatCreditsValue(
  value: number | null,
  locale: string,
  t: Translate,
): string {
  if (value === null) return t('settings.usageScreen.unlimited');
  const normalized = Math.max(value, 0);
  return t('settings.usageScreen.creditCount', {
    count: normalized,
    value: formatCreditAmount(normalized, locale),
  });
}

export type ResetDay = 'today' | 'tomorrow' | 'weekday' | 'date';

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Which phrasing a reset time needs, measured in the viewer's calendar days
 * (not 24-hour blocks): a 4 AM reset seen at 11 PM is "tomorrow".
 * Returns null for a missing, unparseable or past timestamp.
 */
export function classifyResetDay(
  iso: string | null | undefined,
  now: Date = new Date(),
): { day: ResetDay; at: Date } | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms) || ms <= now.getTime()) return null;
  const at = new Date(ms);
  const days = Math.round((startOfLocalDay(at) - startOfLocalDay(now)) / 86_400_000);
  const day: ResetDay =
    days <= 0 ? 'today' : days === 1 ? 'tomorrow' : days < 7 ? 'weekday' : 'date';
  return { day, at };
}

/**
 * "Resets tomorrow at 4:00 AM" in the viewer's locale and time zone, or the
 * plain cadence ("Resets daily" / "Resets monthly") when the server gave no
 * usable future timestamp.
 */
export function formatResetLabel(
  iso: string | null | undefined,
  kind: UsageLimitKind,
  locale: string,
  t: Translate,
  now: Date = new Date(),
): string {
  const reset = classifyResetDay(iso, now);
  if (!reset) {
    return t(kind === 'daily' ? 'settings.resetsDaily' : 'settings.resetsMonthly');
  }
  const time = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(reset.at);
  if (reset.day === 'today') return t('settings.usageScreen.resetsToday', { time });
  if (reset.day === 'tomorrow') return t('settings.usageScreen.resetsTomorrow', { time });
  if (reset.day === 'weekday') {
    const date = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(reset.at);
    return t('settings.usageScreen.resetsOnWeekday', { date, time });
  }
  const date = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(reset.at.getFullYear() !== now.getFullYear() ? { year: 'numeric' as const } : {}),
  }).format(reset.at);
  return t('settings.usageScreen.resetsOn', { date, time });
}

/** Footnote under the Credits card, and whether a quiet upgrade link fits. */
export function selectCreditsFootnote(tier: SubscriptionCreditUsageDisplay['tier']): {
  footnoteKey: string;
  upgrade: { labelKey: string; tier: 'plus' | 'pro' } | null;
} {
  if (tier === 'free') {
    return {
      footnoteKey: 'settings.usageScreen.footnoteFree',
      upgrade: { labelKey: 'settings.usageScreen.upgradeFree', tier: 'plus' },
    };
  }
  if (tier === 'plus') {
    return {
      footnoteKey: 'settings.usageScreen.footnotePaid',
      upgrade: { labelKey: 'settings.usageScreen.upgradePlus', tier: 'pro' },
    };
  }
  return { footnoteKey: 'settings.usageScreen.footnotePaid', upgrade: null };
}
